import React, {
  useEffect,
  useMemo,
  useRef,
  forwardRef,
  useState,
  useImperativeHandle,
} from "react";
import Info from "../assets/icons/ic_info.svg";

const CustomSelect = forwardRef(function CustomSelect(
  {
    label,
    name,
    value,
    onChange,
    placeholder = "Select an option",
    error,
    icon,
    options = [],
    onKeyDown,
    searchable = false,
    className = "",
    required,
    fontWeight = "font-normal",
    textColor = "text-inputColor",
  },
  ref
) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const listRef = useRef(null);

  // Expose imperative API to the parent
  useImperativeHandle(ref, () => ({
    focus: () => buttonRef.current?.focus(),
    open: () => setOpen(true),
    close: () => setOpen(false),
    toggle: () => setOpen((o) => !o),
    scrollIntoView: () => {
      containerRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    },
    node: containerRef.current, // optional, if parent wants the actual node
  }));

  const selected = useMemo(
    () => options.find((o) => o.value === value) || null,
    [options, value]
  );

  const filtered = useMemo(() => {
    if (!searchable || !query) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, searchable, query]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!containerRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const emitChange = (val) => {
    onChange?.({ target: { name, value: val } });
  };

  const handleKeyDown = (e) => {
    onKeyDown?.(e);

    if (
      !open &&
      (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")
    ) {
      e.preventDefault();
      setOpen(true);
      setActiveIndex(0);
      return;
    }
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[activeIndex]) {
        emitChange(filtered[activeIndex].value);
        setOpen(false);
        buttonRef.current?.focus();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      buttonRef.current?.focus();
    }
  };

  return (
    <div className={`mb-4 w-full ${className}`} ref={containerRef}>
      {label && (
        <label className={`block ${textColor} text-sm mb-1 ${fontWeight}`}>
          {label}
          {required && <span className="text-red-500 pl-1">*</span>}{" "}
        </label>
      )}

      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={handleKeyDown}
        className={`relative w-full text-left text-sm font-medium text-activeTabColor border-[0.5px] px-3 py-2 rounded-md focus:outline-none ${
          error
            ? "border-error"
            : "border-inputBorder focus:ring-2 focus:ring-primary"
        } ${icon ? "pr-16" : "pr-10"}`}
        // ring-2 ring-red-300
      >
        <span
          className={
            selected ? "text-gray-900 font-normal" : "text-gray-400 font-normal"
          }
        >
          {selected ? selected.label : placeholder}
        </span>

        {icon && (
          <span className="absolute right-9 top-1/2 -translate-y-1/2 pointer-events-none">
            {typeof icon === "string" ? <img src={icon} alt="icon" /> : icon}
          </span>
        )}

        {/* Chevron with flip */}
        <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
              open ? "rotate-180" : "rotate-0"
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </span>
      </button>

      {open && (
        <div className="relative" onKeyDown={handleKeyDown}>
          <div
            ref={listRef}
            role="listbox"
            tabIndex={-1}
            className="absolute z-50 mt-1 w-full rounded-md border border-inputBorder bg-white shadow-lg"
          >
            {searchable && (
              <div className="p-2 border-b">
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setActiveIndex(0);
                  }}
                  placeholder="Search…"
                  className="w-full border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            )}

            <ul className="max-h-64 overflow-auto rounded-md">
              {filtered.length === 0 && (
                <li className="px-3 py-2 text-sm text-gray-500">No results</li>
              )}

              {filtered.map((opt, idx) => {
                const isActive = idx === activeIndex;
                const isSelected = opt.value === value;
                return (
                  <li
                    key={opt.value}
                    role="option"
                    aria-selected={isSelected}
                    className={`px-3 py-2 text-sm cursor-pointer ${
                      isActive ? "bg-indigo-50" : ""
                    } ${isSelected ? "font-medium" : ""}`}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      emitChange(opt.value);
                      setOpen(false);
                      buttonRef.current?.focus();
                    }}
                  >
                    {opt.label}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {error && (
        <div className="flex gap-1 w-fit text-[10px] bg-red-100 text-red-600 mt-1 rounded p-[3px]">
          <img src={Info} alt="info" />
          {error}
        </div>
      )}
    </div>
  );
});

export default CustomSelect;
